import { padIfSingleDigit } from "./units";

/**
 * Parses an API date value (a `Date`, an ISO date string, or an epoch in ms)
 * into a `Date`, returning `undefined` when absent or unparseable.
 */
export const parseApiDate = (
  value: Date | string | number | null | undefined
): Date | undefined => {
  if (value == null) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Formats a date as `DD.MM.YYYY` (e.g. `10.12.2026`) in local time, matching
 * the Adobe XD spec for the share-link header.
 */
export const formatDateDDMMYYYY = (date: Date): string =>
  `${padIfSingleDigit(date.getDate())}.${padIfSingleDigit(
    date.getMonth() + 1
  )}.${date.getFullYear()}`;

/**
 * Formats a date and time according to the user's timezone.
 * Returns a tuple of [dateString, timeString].
 */
export const formatDateTime = (
  timestamp: Date | undefined,
  timezone: string
): [string, string] => {
  if (!timestamp) {
    return ["", ""];
  }

  try {
    const dateFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const dateParts = dateFormatter.formatToParts(timestamp);
    const timeParts = timeFormatter.formatToParts(timestamp);

    const day = dateParts.find((p) => p.type === "day")?.value ?? "";
    const month = dateParts.find((p) => p.type === "month")?.value ?? "";
    const year = dateParts.find((p) => p.type === "year")?.value ?? "";
    const dateString = `${day}.${month}.${year}`;

    const hour = timeParts.find((p) => p.type === "hour")?.value ?? "";
    const minute = timeParts.find((p) => p.type === "minute")?.value ?? "";
    const timeString = `${hour}:${minute}`;

    return [dateString, timeString];
  } catch (error) {
    // Fallback to local timezone if timezone is invalid
    const date = timestamp.getDate();
    const month = timestamp.getMonth() + 1;
    const year = timestamp.getFullYear();
    const hours = timestamp.getHours();
    const minutes = timestamp.getMinutes();

    const dateString = `${padIfSingleDigit(date)}.${padIfSingleDigit(
      month
    )}.${year}`;
    const timeString = `${padIfSingleDigit(hours)}:${padIfSingleDigit(
      minutes
    )}`;

    return [dateString, timeString];
  }
};
